(() => {
  const drawer = document.querySelector('[data-nm-cart-drawer]');
  if (!drawer || drawer.dataset.initialized === 'true') return;
  drawer.dataset.initialized = 'true';

  const panel = drawer.querySelector('.nm-cart-drawer__panel');
  const itemsContainer = drawer.querySelector('[data-cart-drawer-items]');
  const footer = drawer.querySelector('[data-cart-drawer-footer]');
  const subtotal = drawer.querySelector('[data-cart-drawer-subtotal]');
  const status = drawer.querySelector('[data-cart-drawer-status]');
  const drawerCount = drawer.querySelector('[data-cart-drawer-count]');
  let previouslyFocused = null;

  const endpoint = (url) => `${url.replace(/\.js$/, '')}.js`;

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatMoney = (amount, currency = 'JPY') => new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'JPY' ? 0 : 2,
  }).format(Number(amount || 0) / 100);

  const updateCounts = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
    });
    if (drawerCount) drawerCount.textContent = String(count);
  };

  const emptyMarkup = () => `
    <div class="nm-cart-drawer__empty">
      <span class="nm-cart-drawer__empty-icon" aria-hidden="true">🛒</span>
      <h3>${escapeHtml(drawer.dataset.emptyTitle)}</h3>
      <p>${escapeHtml(drawer.dataset.emptyText)}</p>
      <button class="nm-cart-drawer__continue" type="button" data-cart-drawer-close>
        ${escapeHtml(drawer.dataset.continue)}
      </button>
    </div>
  `;

  const localizeVariantTitle = (title = '') => {
    const normalizedTitle = title.trim().toLowerCase();
    if (normalizedTitle === '2-month care set') return drawer.dataset.variantTwo;
    if (normalizedTitle === '4-month care set') return drawer.dataset.variantFour;
    if (normalizedTitle === '6-month care set') return drawer.dataset.variantSix;
    return title;
  };

  const itemMarkup = (item, currency) => {
    const image = item.featured_image?.url || item.image || '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="nm-cart-drawer__variant">${escapeHtml(localizeVariantTitle(item.variant_title))}</p>`
      : '';
    const imageMarkup = image
      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}" width="240" height="240" loading="lazy">`
      : '';

    return `
      <article class="nm-cart-drawer__item" data-cart-item-key="${escapeHtml(item.key)}">
        <a class="nm-cart-drawer__media" href="${escapeHtml(item.url)}">${imageMarkup}</a>
        <div class="nm-cart-drawer__details">
          <a class="nm-cart-drawer__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variant}
          <div class="nm-cart-drawer__quantity" aria-label="${escapeHtml(drawer.dataset.quantity)}">
            <button type="button" data-cart-decrease aria-label="${escapeHtml(drawer.dataset.decrease)}">−</button>
            <span data-cart-quantity>${item.quantity}</span>
            <button type="button" data-cart-increase aria-label="${escapeHtml(drawer.dataset.increase)}">＋</button>
          </div>
          <button class="nm-cart-drawer__remove" type="button" data-cart-remove>
            ${escapeHtml(drawer.dataset.remove)}
          </button>
        </div>
        <strong class="nm-cart-drawer__line-price">${formatMoney(item.final_line_price, currency)}</strong>
      </article>
    `;
  };

  const renderCart = (cart) => {
    if (!itemsContainer || !footer) return;
    const currency = cart.currency || 'JPY';
    updateCounts(cart.item_count || 0);
    status.textContent = '';

    if (!cart.items?.length) {
      itemsContainer.innerHTML = emptyMarkup();
      footer.hidden = true;
      return;
    }

    itemsContainer.innerHTML = cart.items.map((item) => itemMarkup(item, currency)).join('');
    footer.hidden = false;
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price, currency);
  };

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || drawer.dataset.error);
    return data;
  };

  const refreshCart = () => requestJSON(endpoint(drawer.dataset.cartUrl), { cache: 'no-store' });

  const focusableElements = () => [...panel.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((element) => !element.hidden && element.offsetParent !== null);

  const openDrawer = () => {
    if (drawer.classList.contains('is-open')) return;
    previouslyFocused = document.activeElement;
    drawer.classList.add('nm-cart-drawer-no-motion');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('nm-cart-drawer-open');
    window.requestAnimationFrame(() => {
      drawer.querySelector('.nm-cart-drawer__close')?.focus();
      window.requestAnimationFrame(() => drawer.classList.remove('nm-cart-drawer-no-motion'));
    });
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('nm-cart-drawer-open');
    if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
  };

  const showError = (error) => {
    status.textContent = error?.message || drawer.dataset.error;
  };

  const changeLine = async (key, quantity, itemElement) => {
    itemElement?.classList.add('is-updating');
    status.textContent = '';
    try {
      const cart = await requestJSON(endpoint(drawer.dataset.cartChangeUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      renderCart(cart);
    } catch (error) {
      itemElement?.classList.remove('is-updating');
      showError(error);
    }
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || !drawer) return;
    event.preventDefault();

    const submitButton = event.submitter || form.querySelector('[type="submit"]');
    submitButton?.setAttribute('disabled', 'disabled');
    form.setAttribute('aria-busy', 'true');
    status.textContent = '';

    try {
      await requestJSON(endpoint(drawer.dataset.cartAddUrl), {
        method: 'POST',
        body: new FormData(form),
      });
      const cart = await refreshCart();
      renderCart(cart);
      openDrawer();
    } catch (error) {
      showError(error);
      openDrawer();
    } finally {
      submitButton?.removeAttribute('disabled');
      form.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-cart-drawer-trigger]');
    if (!trigger) return;
    event.preventDefault();
    try {
      renderCart(await refreshCart());
    } catch (error) {
      showError(error);
    }
    openDrawer();
  });

  drawer.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-drawer-close]')) {
      closeDrawer();
      return;
    }

    const item = event.target.closest('[data-cart-item-key]');
    if (!item) return;
    const key = item.dataset.cartItemKey;
    const quantity = Number(item.querySelector('[data-cart-quantity]')?.textContent || 1);

    if (event.target.closest('[data-cart-increase]')) changeLine(key, quantity + 1, item);
    if (event.target.closest('[data-cart-decrease]')) changeLine(key, Math.max(0, quantity - 1), item);
    if (event.target.closest('[data-cart-remove]')) changeLine(key, 0, item);
  });

  document.addEventListener('keydown', (event) => {
    if (!drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = focusableElements();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
